import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { customFetcher } from './customFetcher';

export const IMPERSONATION_STATUS_QUERY_KEY = ['impersonation-status'] as const;

const impersonationStatusSchema = z.object({
    active: z.boolean(),
    impersonated_user: z.object({ id: z.number(), display: z.string() }).optional(),
    impersonator: z.object({ id: z.number(), display: z.string() }).optional(),
});

export type ImpersonationStatus = z.infer<typeof impersonationStatusSchema>;

// Identitäts-Payload (wie Login) für applyAuthSession.
export interface IdentityPayload {
    groups: number[];
    is_superadmin: boolean;
    user_id: number;
}

export interface StopImpersonationPayload extends IdentityPayload {
    last_impersonated_user_id: number;
}

const fetchCsrfToken = async (): Promise<string> => {
    const response = await fetch('/api/csrf/', { credentials: 'include' });
    const data = (await response.json()) as { csrfToken?: string };
    return data.csrfToken ?? '';
};

export const useImpersonationStatus = (options?: { enabled?: boolean }) => {
    const { data, isLoading, refetch } = useQuery<ImpersonationStatus, Error>({
        queryKey: IMPERSONATION_STATUS_QUERY_KEY,
        queryFn: async () =>
            impersonationStatusSchema.parse(await customFetcher<unknown>({ endpoint: '/impersonation/status/' })),
        staleTime: 30 * 1000,
        refetchOnWindowFocus: false,
        enabled: options?.enabled ?? true,
    });
    return { status: data, isLoadingStatus: isLoading, refetchStatus: refetch };
};

export const useStartImpersonation = () => {
    const { mutateAsync: startImpersonation, isPending: isStarting } = useMutation<
        IdentityPayload,
        Error,
        { userId: number }
    >({
        mutationFn: async ({ userId }) => {
            const csrf = await fetchCsrfToken();
            return customFetcher<IdentityPayload>({
                endpoint: '/impersonation/start/',
                method: 'POST',
                body: { user_id: userId },
                headers: { 'X-CSRFToken': csrf },
            });
        },
    });
    return { startImpersonation, isStarting };
};

export const useStopImpersonation = () => {
    const { mutateAsync: stopImpersonation, isPending: isStopping } = useMutation<
        StopImpersonationPayload,
        Error,
        void
    >({
        mutationFn: async () => {
            const csrf = await fetchCsrfToken();
            return customFetcher<StopImpersonationPayload>({
                endpoint: '/impersonation/stop/',
                method: 'POST',
                body: {},
                headers: { 'X-CSRFToken': csrf },
            });
        },
    });
    return { stopImpersonation, isStopping };
};
